/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Module-level handles for the in-flight DBS polling loop.
 *
 * Polling outlives the thunk that starts it (it re-arms itself on a timer),
 * so both the DBS thunks and the DBS reducers — `cancelDbsPolling`,
 * `resetDbsState` — need a way to stop it. They share these handles through
 * this module instead of reaching into each other.
 */
let pollingAbortController: AbortController | null = null;
let pollingTimerId: ReturnType<typeof setTimeout> | null = null;

/**
 * Cancels any polling loop that is still running: aborts the in-flight
 * request and clears the pending retry timer. Safe to call when nothing
 * is polling.
 */
export function stopDbsPolling(): void {
  if (pollingAbortController !== null) {
    pollingAbortController.abort();
    pollingAbortController = null;
  }
  if (pollingTimerId !== null) {
    clearTimeout(pollingTimerId);
    pollingTimerId = null;
  }
}

/**
 * Starts a fresh polling session, cancelling any previous one, and returns
 * the signal that the new session's requests must be issued with.
 */
export function beginDbsPollingSession(): AbortSignal {
  if (pollingAbortController !== null) {
    pollingAbortController.abort();
  }
  pollingAbortController = new AbortController();
  return pollingAbortController.signal;
}

/**
 * Arms the next polling attempt. Keeping the timer handle here is what
 * lets {@link stopDbsPolling} cancel a loop that is between attempts.
 */
export function scheduleNextDbsPoll(
  attempt: () => void,
  delayMs: number
): void {
  pollingTimerId = setTimeout(attempt, delayMs);
}
